import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { CircleDot, GitBranch, GitPullRequest, GitPullRequestCreate } from "lucide-react";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";
import RepoIssues from "./RepoIssues";
import RepoPulls from "./RepoPulls";
import RepoTakken from "./RepoTakken";
import PullSamenvoegen from "./PullSamenvoegen";

/**
 * GL225-4 (besluit Frank 1 nov 2026, keuze A): in Code, boven de bestanden, een balk met Issues, Pull requests, Takken
 * en Pull request maken voor de repo van de open werkruimte, zonder naar Repo's te gaan.
 *
 * Niets dubbel: elke knop toont het bestaande onderdeel van Repo's (RepoIssues, RepoPulls met PullSamenvoegen,
 * RepoTakken) met dezelfde lijsten en acties en dezelfde routes. Pull request maken is het bestaande formulier, open,
 * van de werktak van de werkruimte naar main. Samenvoegen blijft de bevestigkaart; main blijft beschermd (backend).
 * Op de telefoon staan de vier knoppen in twee rijen van twee, zodat er niets buiten beeld valt.
 */
export default function CodeRepoBalk({ workspaceId, repoId, versie = 0, onLosIssueOp = null, onTakWeg = () => {} }) {
  const { t } = useTaal();
  const [repo, setRepo] = useState(null);
  const [werktak, setWerktak] = useState("");
  const [open, setOpen] = useState("");

  const laad = useCallback(() => {
    axios.get(`${API}/repos/${repoId}`).then((r) => setRepo(r.data)).catch(() => setRepo(null));
    axios.get(`${API}/workspaces/${workspaceId}/commits`).then((r) => setWerktak(r.data?.tak || "")).catch(() => {});
  }, [repoId, workspaceId]);
  useEffect(() => { laad(); }, [laad, versie]);

  if (!repo) return null;
  const takken = repo.branches || [];
  const knoppen = [
    ["issues", t("Issues"), CircleDot],
    ["pulls", t("Pull requests"), GitPullRequest],
    ["takken", t("Takken"), GitBranch],
    ["nieuw", t("Pull request maken"), GitPullRequestCreate],
  ];
  return (
    <section className="space-y-2" data-testid="code-repo-balk">
      <div className="grid grid-cols-2 gap-1 sm:grid-cols-4" role="tablist">
        {knoppen.map(([id, naam, Icoon]) => (
          <button key={id} type="button" role="tab" aria-selected={open === id} data-testid={`balk-${id}`}
                  onClick={() => setOpen(open === id ? "" : id)}
                  className={`tap-target flex min-w-0 items-center justify-center gap-1 rounded-md border px-2 py-1.5 text-sm ${
                    open === id ? "bg-muted font-semibold" : "text-muted-foreground hover:bg-muted/50"}`}>
            <Icoon className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="truncate">{naam}</span>
          </button>
        ))}
      </div>
      {open && (
        <div className="min-w-0 rounded-lg border p-2" data-testid={`balk-paneel-${open}`}>
          {open === "issues" && <RepoIssues repo={repo} onLosOp={onLosIssueOp} />}
          {open === "pulls" && <RepoPulls repo={repo} takken={takken} Extra={PullSamenvoegen} onGewijzigd={laad} onTakWeg={onTakWeg} />}
          {open === "takken" && <RepoTakken repo={repo} takken={takken} onTakken={(b) => setRepo({ ...repo, branches: b })}
                                              onTakWeg={onTakWeg} />}
          {open === "nieuw" && (
            <RepoPulls key={`nieuw-${werktak}`} repo={repo} takken={takken} Extra={PullSamenvoegen} onGewijzigd={laad}
                       startNieuw standaardVan={werktak} onTakWeg={onTakWeg} />
          )}
        </div>
      )}
    </section>
  );
}
